import React, { useEffect, useState } from "react";
import "./PublicationSynchronization.scss";

import axios from "axios";
import { uuidv7 as uuid } from "uuidv7";
import { useDispatch, useSelector } from "react-redux";

import classNames from "classnames";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { useIntl } from "react-intl";
import { translateMessage } from "src/app/methods/translateMessage";
import AnimatedDiv from "src/app/components/AnimatedDiv/AnimatedDiv";
import { SettingsButtons } from "src/app/SettingsModal/components/SettingsTabContent/SettingsTabContent";
import { getProject } from "src/redux";
import { RootState } from "src/redux/reducers";
import { showErrorToast } from "src/utils/methods";
import { API_URLS } from "../../../../../../utils/API_URLS";
import { showToast } from "../../../../../methods/showToast";
import { refreshAutomationList } from "../../../../../../redux/automation/automationActions";

import { ModalText, ModalTitle } from "../../../../../components/Modal/Modal";
import { Button } from "../../../../../components/Button/Button";
import { CustomInputLabel } from "../../../../../components/CustomInput/CustomInput";

import { ReactComponent as LighteningIcon } from "../../../../../../images/lightening.svg";
import { ReactComponent as PowerIcon } from "../../../../../../images/power.svg";
import { ReactComponent as SaveIcon } from "../../../../../../images/floppy-disk.svg";
import CustomSelect from "../../../../../components/CustomSelect/CustomSelect";
import { AutomationsSection } from "../../Automations";
import AutomationsEnum from "../../AutomationsEnum";

interface Props {
  setSection: (section: AutomationsSection) => void;
  basicData?: any;
}

type FrequencyOption = {
  label: React.ReactNode;
  value: {
    activeFor: number;
    refreshInterval: number;
  };
};

const renderFrequencyLabel = (activeFor: number, refreshInterval: number) => {
  return (
    <>
      {activeFor}&nbsp;
      <IDHFormattedMessage
        id="ws_days_after_publication"
        defaultMessage="days after publication"
      />
      &nbsp;-&nbsp;
      <IDHFormattedMessage
        id="ws_updates_once_per"
        defaultMessage="updates once per"
      />
      &nbsp;{refreshInterval}&nbsp;
      <IDHFormattedMessage id="ws_hours" defaultMessage="hours" />
    </>
  );
};

const frequencyOptions: FrequencyOption[] = [
  {
    label: (
      <>
        <IDHFormattedMessage
          id="ws_1_day_after_publication"
          defaultMessage="1 day after publication"
        />
        &nbsp;-&nbsp;
        <IDHFormattedMessage
          id="ws_updates_once_per_hour"
          defaultMessage="updates once per hour"
        />
      </>
    ),
    value: {
      activeFor: 1,
      refreshInterval: 1,
    },
  },
  {
    label: renderFrequencyLabel(3, 2),
    value: {
      activeFor: 3,
      refreshInterval: 2,
    },
  },
  {
    label: renderFrequencyLabel(5, 6),
    value: {
      activeFor: 5,
      refreshInterval: 6,
    },
  },
  {
    label: renderFrequencyLabel(7, 12),
    value: {
      activeFor: 7,
      refreshInterval: 12,
    },
  },
  {
    label: renderFrequencyLabel(30, 24),
    value: {
      activeFor: 30,
      refreshInterval: 24,
    },
  },
];

export const PublicationsSynchronization: React.FC<Props> = (props) => {
  const { setSection, basicData } = props;

  const [frequency, setFrequency] = useState<FrequencyOption | null>(null);
  const [additionalOption, setAdditionalOption] =
    useState<FrequencyOption | null>(null);

  // loading
  const [createLoading, setCreateLoading] = useState(false);
  const [enableLoading, setEnableLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const { projectId } = basicData;

  const {
    automationReducer: { editedAutomationData },
  } = useSelector((state: RootState) => state);

  const dispatch = useDispatch();
  const intl = useIntl();

  const goBack = () => setSection(AutomationsSection.AutomationsMain);

  useEffect(() => {
    const { activeFor, refreshInterval } = editedAutomationData?.settings || {};

    if (!activeFor || !refreshInterval) {
      setFrequency(null);
      setAdditionalOption(null);
      return;
    }

    const foundFrequencyOption = frequencyOptions?.find(
      (item) =>
        item.value.activeFor ===
          (editedAutomationData.settings?.activeFor ?? 7) &&
        item.value.refreshInterval ===
          (editedAutomationData.settings?.refreshInterval ?? 12),
    );

    if (foundFrequencyOption) {
      setFrequency(foundFrequencyOption);
      setAdditionalOption(null);
      return;
    }

    const additionalOption = {
      label: renderFrequencyLabel(activeFor, refreshInterval),
      value: {
        activeFor,
        refreshInterval,
      },
    };

    setFrequency(additionalOption);
    setAdditionalOption(additionalOption);
  }, [editedAutomationData]);

  const reloadData = () => {
    dispatch(refreshAutomationList(projectId || basicData.projectId));
    dispatch(getProject(projectId || basicData.projectId));
  };

  const handleErrors = (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.data?.errors) {
      const key = error.response?.data?.errors[0];
      showErrorToast({
        id: key,
        defaultMessage: key,
      });
    } else {
      showErrorToast();
    }
  };

  const refreshIntervalEqual =
    frequency?.value.refreshInterval ===
    editedAutomationData.settings?.refreshInterval;
  const activeForEqual =
    frequency?.value.activeFor === editedAutomationData.settings?.activeFor;

  const differencesDetected =
    editedAutomationData.uuid && (!refreshIntervalEqual || !activeForEqual);

  const toggleAutomation = async () => {
    setEnableLoading(true);
    const newValue = !editedAutomationData.enabled;

    try {
      const toggleUrl = API_URLS.toggleAutomation;

      const payload: any = {
        uuid: editedAutomationData.uuid,
        enabled: newValue,
      };

      if (differencesDetected) {
        payload.settings = {
          refreshInterval: frequency?.value.refreshInterval,
          activeFor: frequency?.value.activeFor,
        };
        payload.wsAutomationName =
          AutomationsEnum.PublicationSynchronizationRunnableAutomation;
      }

      await axios.post(toggleUrl, payload);

      if (newValue) {
        showToast(
          "info",
          <IDHFormattedMessage
            id="ws_automation_enabled"
            defaultMessage="Automation enabled"
          />,
          <IDHFormattedMessage
            id="ws_we_will_inform_you_about_detected_publications"
            defaultMessage="We will inform you about detected publications."
          />,
        );
        runAutomation();
      } else {
        showToast(
          "success",
          <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
          <IDHFormattedMessage
            id="ws_automation_disabled"
            defaultMessage="Automation disabled."
          />,
        );
      }
      goBack();
      reloadData();
    } catch (err) {
      console.error(err);
      handleErrors(err);
    } finally {
      setEnableLoading(false);
    }
  };

  const saveAutomationChanges = async () => {
    setSaveLoading(true);
    const url = API_URLS.updateAutomation;

    try {
      await axios.put(url, {
        uuid: editedAutomationData.uuid,
        wsProjectUuid: projectId,
        wsAutomationName: AutomationsEnum.PublicationSynchronizationRunnableAutomation,
        settings: {
          refreshInterval: frequency?.value.refreshInterval,
          activeFor: frequency?.value.activeFor,
        },
      });
      reloadData();
      showToast(
        "success",
        <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
        <IDHFormattedMessage
          id="ws_changes_saved_successfully"
          defaultMessage="Changes saved successfully."
        />,
      );
    } catch (error) {
      console.error(error);
      handleErrors(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const runAutomation = async () => {
    const url = API_URLS.runAutomation;

    try {
      await axios.post(url, {
        uuid: editedAutomationData.uuid,
        wsProjectUuid: projectId || basicData.projectId,
        wsAutomationName: AutomationsEnum.PublicationSynchronizationRunnableAutomation,
        settings: {
          refreshInterval: frequency?.value.refreshInterval,
          activeFor: frequency?.value.activeFor,
        },
      });
    } catch (err) {
      console.error(err);
      handleErrors(err);
    }
  };

  const createAutomation = async () => {
    setCreateLoading(true);
    const url = API_URLS.createAutomation;
    const automationUuid = uuid();

    try {
      await axios.post(url, {
        uuid: automationUuid,
        wsProjectUuid: projectId || basicData.projectId,
        wsAutomationName: AutomationsEnum.PublicationSynchronizationRunnableAutomation,
        settings: {
          refreshInterval: frequency?.value.refreshInterval,
          activeFor: frequency?.value.activeFor,
        },
      });
      setCreateLoading(false);
      reloadData();
      goBack();
    } catch (error) {
      console.error(error);
      handleErrors(error);
    }
  };

  const options = [
    ...frequencyOptions,
    ...(additionalOption != null ? [additionalOption] : []),
  ];

  return (
    <>
      <AnimatedDiv className="publication-synchronization">
        <span className="publication-synchronization__link" onClick={goBack}>
          <IDHFormattedMessage
            id="ws_automations"
            defaultMessage="Automations"
          />
        </span>
        <ModalTitle>
          <IDHFormattedMessage
            id="ws_publications_synchronization"
            defaultMessage="Publications synchronization"
          />
          <span
            className={classNames([
              "publication-synchronization__label",
              editedAutomationData?.enabled &&
                "publication-synchronization__label--active",
            ])}
          >
            {editedAutomationData?.enabled ? (
              <IDHFormattedMessage id="ws_active" defaultMessage="Active" />
            ) : (
              <IDHFormattedMessage id="ws_disabled" defaultMessage="Disabled" />
            )}
          </span>
        </ModalTitle>
        <ModalText>
          <IDHFormattedMessage
            id="ws_publications_synchronization_modal_text"
            defaultMessage="Please specify the frequency at which each publication should be refreshed."
          />
        </ModalText>

        <div className="publication-synchronization__row">
          <CustomInputLabel htmlFor="frequency">
            <IDHFormattedMessage
              id="ws_update_stats_for"
              defaultMessage="Update stats for"
            />
          </CustomInputLabel>
          <CustomSelect
            id="frequency"
            options={options}
            value={frequency}
            onChange={setFrequency}
            placeholder={`${translateMessage({
              intl,
              id: "ws_select_frequency",
              defaultMessage: "Select frequency",
            })}...`}
            disabled={saveLoading}
            noOptionsMessage="Add at least one publication to use it in report"
          />
        </div>
      </AnimatedDiv>
      <SettingsButtons>
        <div className="publication-synchronization__buttons">
          <div className="publication-synchronization__buttons-wrapper">
            <Button
              className="publication-synchronization__buttons-back"
              size="large"
              variant="white-with-border"
              onClick={goBack}
            >
              <IDHFormattedMessage id="ws_back" defaultMessage="Back" />
            </Button>
          </div>
          <div className="publication-synchronization__buttons-wrapper publication-synchronization__buttons-wrapper--multiple">
            {differencesDetected ? (
              <Button
                size="large"
                variant="white-with-border"
                onClick={saveAutomationChanges}
                disabled={saveLoading || !frequency}
              >
                <SaveIcon />
                <IDHFormattedMessage id="ws_save" defaultMessage="Save" />
              </Button>
            ) : null}

            {editedAutomationData.enabled === true ? (
              <Button
                size="large"
                variant="blue"
                onClick={toggleAutomation}
                disabled={enableLoading || !frequency}
              >
                <PowerIcon />
                <IDHFormattedMessage id="ws_disable" defaultMessage="Disable" />
              </Button>
            ) : null}

            {editedAutomationData.enabled === false ? (
              <Button
                size="large"
                variant="blue"
                onClick={toggleAutomation}
                disabled={enableLoading || !frequency}
              >
                <LighteningIcon />
                <IDHFormattedMessage
                  id="ws_enable_run_now"
                  defaultMessage="Enable & run now"
                />
              </Button>
            ) : null}

            {!editedAutomationData.uuid ? (
              <Button
                size="large"
                variant="blue"
                onClick={createAutomation}
                disabled={!frequency || createLoading}
              >
                <LighteningIcon />
                <IDHFormattedMessage
                  id="ws_enable_run_now"
                  defaultMessage="Enable & run now"
                />
              </Button>
            ) : null}
          </div>
        </div>
      </SettingsButtons>
    </>
  );
};
